import { Component, HostListener, Inject, OnDestroy } from '@angular/core';
import { Tierarzt } from '../../../../../../projects/kiene-core/src/lib/components/tieraerzte/classes/tierarzt';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { API_BASE_URL_SERVICE } from '../../../../../../projects/kiene-core/src/lib/kiene-core.config';
import { KieneTableService } from '../../../../components/kiene-table/kiene-table.service';
import { MessageService } from '../../../../../../projects/kiene-core/src/lib/services/message-service/message.service';
import { KieneUtils } from '../../../../../../projects/kiene-core/src/lib/utils/KieneUtils';
import { TierwohlprotokollSchwein } from '../classes/tierwohlprotokoll-schwein';
import { LocalStorageService } from '../../../../../../projects/kiene-core/src/lib/services/local-storage/local-storage.service';
import { Client } from '../../../../../../projects/kiene-core/src/lib/components/client/classes/client';
import { FormControl } from '@angular/forms';
import { applyParsedTierwohlDates, withFormattedTierwohlDates } from '../../twp-date';

/** 0/1-Flags aus der API oft als String "0"/"1" – fuer Radios/Checkboxen normalisieren. */
const TWP_SCHWEIN_FLAG_FIELDS: (keyof TierwohlprotokollSchwein)[] = [
    'gesundheitsstatus_prrs',
    'gesundheitsstatus_app',
    'bestand_klinisch_ohne_befund',
    'hinweise_tierseuchen',
    'mangagementfaktor_haltungssystem',
    'managementfaktor_fuetterung_wasserversorgung',
    'managementfaktor_betrieb',
    'managementfaktor_hygiene',
    'managementfaktor_zucht',
    'managementfaktor_sonstiges',
    'gesundheitsstatus_atmungsorgane',
    'gesundheitsstatus_verdauungsapparat',
    'gesundheitsstatus_bewegungsapparat',
    'gesundheitsstatus_haut_kondition',
    'gesundheitsstatus_sonstiges',
    'diagnostische_massnahmen_sektion',
    'diagnostische_massnahmen_kot',
    'diagnostische_massnahmen_resistenztest',
    'diagnostische_massnahmen_hautgeschabsel',
    'diagnostische_massnahmen_blut',
    'diagnostische_massnahmen_futter_wasser',
    'diagnostische_massnahmen_tupfer',
    'diagnostische_massnahmen_sonstiges',
    'salmonellen_monitoring',
    'antibiotika_monitoring',
    'schlacht_befunddaten',
    'betriebliche_eigenkontrolle',
    'tierschutzindikatoren',
    'stallklima_kontrolliert',
    'impfprogramm_influenza',
    'impfprogramm_prrs',
    'impfprogramm_rotlauf',
    'impfprogramm_circo',
    'impfprogramm_rhinitis_atr',
    'impfprogramm_ileitis',
    'impfprogramm_haemophilus',
    'impfprogramm_mycoplasmen',
    'impfprogramm_app',
    'impfprogramm_parvo',
    'impfprogramm_sonstiges',
    'parasitologische_massnahmen_endoparasiten',
    'parasitologische_massnahmen_ektoparasiten',
    'plan_tiergesundheit_hygienemanagement',
    'massnahmenplan_einzelaktivitaeten',
    'abrechnen',
];

@Component({
    selector: 'kiene-tierwohlprotokoll-schwein-details',
    templateUrl: './tierwohlprotokoll-schwein-details.component.html',
    styleUrls: ['./tierwohlprotokoll-schwein-details.component.scss'],
})
export class TierwohlprotokollSchweinDetailsComponent implements OnDestroy {
    protected readonly KieneUtils = KieneUtils;

    tierwohlprotokollSchwein: TierwohlprotokollSchwein;
    selectedKunde: Client;
    selectedTierarzt: Tierarzt;
    userIstTierarzt = false;
    abrechnenCtrl = new FormControl(true);
    private autosaveHandle?: number;
    private isSavingDraft = false;
    private autosaveReady = false;

    constructor(
        public dialogRef: MatDialogRef<TierwohlprotokollSchweinDetailsComponent>,
        @Inject(MAT_DIALOG_DATA) private data,
        @Inject(API_BASE_URL_SERVICE) private apiBaseUrl,
        private api: KieneTableService,
        private messageService: MessageService,
        private localStorageService: LocalStorageService,
    ) {
        if (data?.tierwohlprotokollSchwein) {
            this.tierwohlprotokollSchwein = data?.tierwohlprotokollSchwein;
            this.normalizeApiFlags(this.tierwohlprotokollSchwein);
            applyParsedTierwohlDates(this.tierwohlprotokollSchwein);
            this.generateObjsForAutocompletes();
        } else {
            this.tierwohlprotokollSchwein = new TierwohlprotokollSchwein();
            this.tierwohlprotokollSchwein.status_id = 153;
        }

        if (KieneUtils.isApiZero(this.tierwohlprotokollSchwein.abrechnen)) {
            this.abrechnenCtrl.setValue(false);
        } else if (KieneUtils.isApiOne(this.tierwohlprotokollSchwein.abrechnen)) {
            this.abrechnenCtrl.setValue(true);
        }
        if (this.tierwohlprotokollSchwein.abgabebeleg_id) {
            this.abrechnenCtrl.disable();
        }

        if (!this.tierwohlprotokollSchwein.datum) {
            this.tierwohlprotokollSchwein.datum = new Date();
        }

        if (localStorageService.getCurrentClient()) {
            this.selectedKunde = localStorageService.getCurrentClient();
            this.setKunde(this.selectedKunde);
        }

        this.userIstTierarzt = localStorageService.isCurrentUserVeterinarian();
        if (this.userIstTierarzt) {
            const session = localStorageService.getCurrentUser();
            this.selectedTierarzt = new Tierarzt();
            this.selectedTierarzt.tierarzt_id = session.tierarzt_id;
            this.selectedTierarzt.vorname = session.vorname;
            this.selectedTierarzt.nachname = session.nachname;
            this.setTierarzt(this.selectedTierarzt);
        }
        this.autosaveReady = true;
    }

    ngOnDestroy(): void {
        this.clearAutosaveTimer();
    }

    @HostListener('input')
    @HostListener('change')
    onAutosaveInput(): void {
        this.scheduleAutosave();
    }

    save() {
        this.clearAutosaveTimer();
        this.persist(false);
    }

    private scheduleAutosave(): void {
        if (!this.autosaveReady || KieneUtils.apiStatusId(this.tierwohlprotokollSchwein?.status_id, 154)) {
            return;
        }
        this.clearAutosaveTimer();
        this.autosaveHandle = window.setTimeout(() => this.persist(true), 1500);
    }

    private clearAutosaveTimer(): void {
        if (this.autosaveHandle) {
            window.clearTimeout(this.autosaveHandle);
            this.autosaveHandle = undefined;
        }
    }

    private persist(silent: boolean): void {
        if (KieneUtils.apiStatusId(this.tierwohlprotokollSchwein?.status_id, 154)) {
            return;
        }
        if (this.isSavingDraft) {
            if (silent) {
                this.scheduleAutosave();
            }
            return;
        }
        this.normalizeNullableDates(this.tierwohlprotokollSchwein);
        this.normalizeNullableAnzahlen(this.tierwohlprotokollSchwein);
        this.tierwohlprotokollSchwein.abrechnen = this.abrechnenCtrl.value ? 1 : 0;
        if (!this.tierwohlprotokollSchwein.status_id) {
            this.tierwohlprotokollSchwein.status_id = 153;
        }
        if (!this.tierwohlprotokollSchwein.kunde_id) {
            if (!silent) {
                this.messageService.alertMessage('Bitte einen Betrieb auswählen!');
            }
            return;
        }
        this.isSavingDraft = true;
        const isCreate = !this.tierwohlprotokollSchwein.twp_schwein_id;
        const onOk = (response?: TierwohlprotokollSchwein) => {
            this.isSavingDraft = false;
            if (response?.twp_schwein_id) {
                this.tierwohlprotokollSchwein.twp_schwein_id = response.twp_schwein_id;
            }
            if (!silent) {
                this.messageService.infoMessage(
                    isCreate
                        ? 'Tierwohlprotokoll wurde erfolgreich erstellt.'
                        : 'Tierwohlprotokoll wurde erfolgreich aktualisiert.',
                );
                this.dialogRef.close(true);
            }
        };
        const onErr = (error: any) => {
            this.isSavingDraft = false;
            if (!silent) {
                this.messageService.errorMessage('Fehler beim Speichern des Tierwohlprotokolls!', error);
            }
        };
        const payload = withFormattedTierwohlDates(this.tierwohlprotokollSchwein);
        if (this.tierwohlprotokollSchwein.twp_schwein_id) {
            this.api
                .updateEntity(this.apiBaseUrl + 'tierwohlprotokoll_schwein/update.php', payload)
                .subscribe({ next: onOk, error: onErr });
        } else {
            this.api
                .post(this.apiBaseUrl + 'tierwohlprotokoll_schwein/create.php', payload)
                .subscribe({ next: onOk, error: onErr });
        }
    }

    private normalizeNullableDates(twp: {
        datum?: Date | string | null;
        durchfuehrung_bis?: Date | string | null;
        naechster_bestandsbesuch?: Date | string | null;
        parasitologische_massnahmen_endoparasiten_zuletzt?: Date | string | null;
        parasitologische_massnahmen_ektoparasiten_zuletzt?: Date | string | null;
    }) {
        const dateKeys = [
            'datum',
            'durchfuehrung_bis',
            'naechster_bestandsbesuch',
            'parasitologische_massnahmen_endoparasiten_zuletzt',
            'parasitologische_massnahmen_ektoparasiten_zuletzt',
        ] as const;
        for (const key of dateKeys) {
            const value = twp[key];
            if (value == null || value === '' || (value instanceof Date && Number.isNaN(value.getTime()))) {
                twp[key] = null;
            }
        }
    }

    /** Leere Anzahl-Felder explizit als null senden, sonst bleibt der alte Wert in der DB. */
    private normalizeNullableAnzahlen(twp: TierwohlprotokollSchwein): void {
        const keys: (keyof TierwohlprotokollSchwein)[] = [
            'anzahl_sauen',
            'anzahl_eber',
            'anzahl_ferkel',
            'anzahl_mastschweine',
            'biologisch_wurfzahl_abgegangene_sauen',
            'biologisch_lebendgeborene_ferkel',
            'biologisch_aufgezogene_ferkel',
            'biologisch_tageszunahmen_mastschweine',
        ];
        for (const key of keys) {
            const value = twp[key] as unknown;
            if (value === '' || value === undefined) {
                (twp as any)[key] = null;
            }
        }
    }

    setKunde(c: Client) {
        this.tierwohlprotokollSchwein.kunde_id = c.kunde_id;
        this.tierwohlprotokollSchwein.kunde = c.bezeichnung_final;
        this.tierwohlprotokollSchwein.kunde_vvvo = c.vvvo;
        this.tierwohlprotokollSchwein.kunde_strasse = c.strasse_final;
        this.tierwohlprotokollSchwein.kunde_plz = c.plz_final;
        this.tierwohlprotokollSchwein.kunde_ort = c.ort_final;
        this.scheduleAutosave();
    }

    setTierarzt(t: Tierarzt) {
        this.tierwohlprotokollSchwein.tierarzt_id = t.tierarzt_id;
        this.tierwohlprotokollSchwein.tierarzt_vorname = t.vorname;
        this.tierwohlprotokollSchwein.tierarzt_nachname = t.nachname;
        this.scheduleAutosave();
    }

    private normalizeApiFlags(twp: TierwohlprotokollSchwein) {
        for (const key of TWP_SCHWEIN_FLAG_FIELDS) {
            const value = twp[key];
            if (value == null || value === ('' as never)) {
                (twp as any)[key] = null;
            } else if (KieneUtils.isApiOne(value)) {
                (twp as any)[key] = 1;
            } else if (KieneUtils.isApiZero(value)) {
                (twp as any)[key] = 0;
            }
        }
        if (twp.status_id != null && twp.status_id !== ('' as never)) {
            twp.status_id = Number(twp.status_id);
        }
    }

    private generateObjsForAutocompletes() {
        if (this.tierwohlprotokollSchwein?.kunde_id) {
            this.loadKunde(this.tierwohlprotokollSchwein?.kunde_id).subscribe({
                next: value => {
                    this.selectedKunde = value;
                },
                error: () => {
                    this.selectedKunde = new Client();
                    this.selectedKunde.kunde_id = this.tierwohlprotokollSchwein.kunde_id;
                    this.selectedKunde.bezeichnung_final = this.tierwohlprotokollSchwein.kunde;
                    this.selectedKunde.vvvo = this.tierwohlprotokollSchwein.kunde_vvvo;
                    this.selectedKunde.strasse_final = this.tierwohlprotokollSchwein.kunde_strasse;
                    this.selectedKunde.plz_final = this.tierwohlprotokollSchwein.kunde_plz;
                    this.selectedKunde.ort_final = this.tierwohlprotokollSchwein.kunde_ort;
                },
            });
        }

        if (this.tierwohlprotokollSchwein?.tierarzt_id) {
            this.loadTierarzt(this.tierwohlprotokollSchwein.tierarzt_id).subscribe({
                next: value => {
                    this.selectedTierarzt = value;
                },
                error: () => {
                    this.selectedTierarzt = new Tierarzt();
                    this.selectedTierarzt.tierarzt_id = this.tierwohlprotokollSchwein.tierarzt_id;
                    this.selectedTierarzt.vorname = this.tierwohlprotokollSchwein.tierarzt_vorname;
                    this.selectedTierarzt.nachname = this.tierwohlprotokollSchwein.tierarzt_nachname;
                },
            });
        }
    }

    private loadKunde(kunde_id: number) {
        return this.api.getOne(this.apiBaseUrl + 'kunde/read_one.php', 'kunde_id', kunde_id.toString());
    }

    private loadTierarzt(tierarzt_id: number) {
        return this.api.getOne(this.apiBaseUrl + 'tierarzt/read_one.php', 'tierarzt_id', tierarzt_id.toString());
    }
}
